import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { AlertTriangle, Send, Loader2, Info, AlertCircle, Flame, Search, X, FileText, UserSquare } from 'lucide-react';
import { toast } from 'sonner';
import { MultiRecipientPicker } from './MultiRecipientPicker';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const SEVERITY_OPTIONS = [
  { key: 'info', label: 'Informativo', icon: Info, color: 'text-sky-600 bg-sky-50 border-sky-200' },
  { key: 'aviso', label: 'Aviso', icon: AlertCircle, color: 'text-amber-700 bg-amber-50 border-amber-200' },
  { key: 'critico', label: 'Crítico', icon: Flame, color: 'text-rose-700 bg-rose-50 border-rose-300' },
];

export const SendAlertModal = ({ open, onOpenChange }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [partners, setPartners] = useState([]);
  const [users, setUsers] = useState([]);
  const [roleFilter, setRoleFilter] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState(new Set());
  const [severity, setSeverity] = useState('aviso');
  const [message, setMessage] = useState('');

  const [contractQuery, setContractQuery] = useState('');
  const [contractResults, setContractResults] = useState([]);
  const [contractSelected, setContractSelected] = useState(null);
  const [contractSearching, setContractSearching] = useState(false);
  const [leadQuery, setLeadQuery] = useState('');
  const [leadResults, setLeadResults] = useState([]);
  const [leadSelected, setLeadSelected] = useState(null);
  const [leadSearching, setLeadSearching] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setSelectedUserIds(new Set());
    axios.get(`${API_URL}/admin-alerts/recipients`)
      .then(r => {
        setPartners(r.data.partners || []);
        setUsers(r.data.users || []);
        setRoleFilter(r.data.role_filter || []);
      })
      .catch(err => toast.error(err?.response?.data?.detail || 'Erro ao carregar destinatários'))
      .finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const q = (contractQuery || '').trim();
    if (q.length < 2 || contractSelected) { setContractResults([]); return; }
    const t = setTimeout(async () => {
      setContractSearching(true);
      try {
        const r = await axios.get(`${API_URL}/contracts`, { params: { search: q } });
        setContractResults((r.data || []).slice(0, 10));
      } catch { setContractResults([]); } finally { setContractSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [contractQuery, contractSelected, open]);

  useEffect(() => {
    if (!open) return;
    const q = (leadQuery || '').trim();
    if (q.length < 2 || leadSelected) { setLeadResults([]); return; }
    const t = setTimeout(async () => {
      setLeadSearching(true);
      try {
        const r = await axios.get(`${API_URL}/leads`, { params: { search: q } });
        setLeadResults((r.data || []).slice(0, 10));
      } catch { setLeadResults([]); } finally { setLeadSearching(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [leadQuery, leadSelected, open]);

  const reset = () => {
    setSelectedUserIds(new Set());
    setSeverity('aviso'); setMessage('');
    setContractQuery(''); setContractResults([]); setContractSelected(null);
    setLeadQuery(''); setLeadResults([]); setLeadSelected(null);
  };

  const handleSubmit = async () => {
    const ids = [...selectedUserIds];
    if (ids.length === 0) { toast.error('Seleciona pelo menos 1 destinatário'); return; }
    if ((message || '').trim().length < 5) { toast.error('Mensagem mínima de 5 caracteres'); return; }
    setSubmitting(true);
    try {
      const payload = {
        message: message.trim(),
        severity,
        recipient_ids: ids,
        contract_id: contractSelected?.id || null,
        lead_id: leadSelected?.id || null,
      };
      const r = await axios.post(`${API_URL}/admin-alerts`, payload);
      const count = r.data?.count || ids.length;
      toast.success(count > 1 ? `Alerta enviado a ${count} utilizadores` : 'Alerta enviado');
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao enviar alerta');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="send-alert-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-600" />
            Enviar Alerta Forte
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-slate-500">
            Os destinatários verão um popup vermelho em <strong>tela cheia</strong> que não pode fechar até responderem com pelo menos 10 caracteres.
          </p>

          {/* Severity */}
          <div>
            <Label className="text-sm">Severidade</Label>
            <div className="grid grid-cols-3 gap-2 mt-1.5">
              {SEVERITY_OPTIONS.map(opt => {
                const Icon = opt.icon;
                const active = severity === opt.key;
                return (
                  <button
                    type="button"
                    key={opt.key}
                    onClick={() => setSeverity(opt.key)}
                    className={`flex flex-col items-center gap-1 px-3 py-3 rounded-lg border-2 transition-all ${active ? opt.color + ' ring-2 ring-offset-1 ring-current' : 'border-slate-200 text-slate-500 hover:border-slate-300'}`}
                    data-testid={`severity-${opt.key}`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{opt.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Multi-recipient picker */}
          <div>
            <Label className="text-sm">Destinatários *</Label>
            <div className="mt-1.5">
              <MultiRecipientPicker
                partners={partners}
                users={users}
                roleFilter={roleFilter}
                selectedUserIds={selectedUserIds}
                onChange={setSelectedUserIds}
                accent="rose"
                loading={loading}
              />
            </div>
          </div>

          {/* Message */}
          <div>
            <Label className="text-sm">Mensagem *</Label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              placeholder="Descreve o que está mal e o que precisa de resposta..."
              data-testid="alert-message-textarea"
            />
          </div>

          {/* Contract picker */}
          <div>
            <Label className="text-sm flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> Contrato relacionado (opcional)</Label>
            {contractSelected ? (
              <div className="flex items-center justify-between mt-1 px-3 py-2 border border-indigo-300 bg-indigo-50 rounded-md">
                <span className="text-sm text-indigo-800 truncate">
                  #{contractSelected.codigo} · {contractSelected.nome_cliente}
                </span>
                <button type="button" onClick={() => { setContractSelected(null); setContractQuery(''); }} className="text-indigo-600 hover:text-indigo-800" data-testid="alert-clear-contract">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="mt-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={contractQuery} onChange={(e) => setContractQuery(e.target.value)} placeholder="Pesquisar por código, nome, NIF..." className="pl-9" data-testid="alert-contract-search" />
                </div>
                {contractQuery.trim().length >= 2 && (
                  <div className="border border-slate-100 rounded-md mt-1 max-h-40 overflow-y-auto" data-testid="alert-contract-results">
                    {contractSearching ? (
                      <div className="text-xs text-slate-400 py-2 text-center"><Loader2 className="w-3 h-3 inline animate-spin" /> A pesquisar...</div>
                    ) : contractResults.length === 0 ? (
                      <div className="text-xs text-slate-400 py-2 text-center">Sem resultados</div>
                    ) : (
                      <ul className="divide-y divide-slate-50">
                        {contractResults.map(c => (
                          <li key={c.id} onClick={() => { setContractSelected({ id: c.id, codigo: c.codigo, nome_cliente: c.nome_cliente }); setContractQuery(''); }} className="px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-xs" data-testid={`alert-contract-result-${c.id}`}>
                            <span className="font-semibold">#{c.codigo}</span> · {c.nome_cliente} · {c.company || c.company_name}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Lead picker */}
          <div>
            <Label className="text-sm flex items-center gap-1.5"><UserSquare className="w-3.5 h-3.5" /> Lead relacionada (opcional)</Label>
            {leadSelected ? (
              <div className="flex items-center justify-between mt-1 px-3 py-2 border border-emerald-300 bg-emerald-50 rounded-md">
                <span className="text-sm text-emerald-800 truncate">
                  {leadSelected.codigo ? `#${leadSelected.codigo} · ` : ''}{leadSelected.client_name}
                </span>
                <button type="button" onClick={() => { setLeadSelected(null); setLeadQuery(''); }} className="text-emerald-600 hover:text-emerald-800" data-testid="alert-clear-lead">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="mt-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={leadQuery} onChange={(e) => setLeadQuery(e.target.value)} placeholder="Pesquisar por nome, NIF, telefone..." className="pl-9" data-testid="alert-lead-search" />
                </div>
                {leadQuery.trim().length >= 2 && (
                  <div className="border border-slate-100 rounded-md mt-1 max-h-40 overflow-y-auto" data-testid="alert-lead-results">
                    {leadSearching ? (
                      <div className="text-xs text-slate-400 py-2 text-center"><Loader2 className="w-3 h-3 inline animate-spin" /> A pesquisar...</div>
                    ) : leadResults.length === 0 ? (
                      <div className="text-xs text-slate-400 py-2 text-center">Sem resultados</div>
                    ) : (
                      <ul className="divide-y divide-slate-50">
                        {leadResults.map(l => (
                          <li key={l.id} onClick={() => { setLeadSelected({ id: l.id, codigo: l.codigo, client_name: l.client_name }); setLeadQuery(''); }} className="px-3 py-1.5 hover:bg-slate-50 cursor-pointer text-xs" data-testid={`alert-lead-result-${l.id}`}>
                            {l.codigo && <span className="font-semibold">#{l.codigo} · </span>}{l.client_name} · {l.client_phone || '—'}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || selectedUserIds.size === 0 || message.trim().length < 5}
            className="bg-rose-600 hover:bg-rose-700 gap-2"
            data-testid="alert-submit-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {selectedUserIds.size > 1 ? `Enviar a ${selectedUserIds.size} utilizadores` : 'Enviar Alerta'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SendAlertModal;
